import { create } from "zustand"
import { api } from "../api/client"
import type {
  ActionFileContext,
  ActionPhase,
  ActionScopeMapping,
  ActionState,
  ActionType,
  DiffCell,
  DiffState,
  PkSet,
  RecordSelection,
  RollupState,
  ScopeDiffState,
  ScopeListItem,
  SelectMapping,
} from "../types"

export interface FilterState {
  originScopeId: string
  pkValues: string[]
}

interface UIState {
  activeScopeId: string
  scopes: ScopeListItem[]

  // Per-record panels
  detailRecordId: string | null // unified detail side-panel
  editRecordId: string | null // null = closed, 'new' = creating (modal)
  deleteRecordId: string | null
  relationPreview: { recordId: string; primName: string } | null

  // Ingestion modal (optional recordId for single-record ingestion from detail panel)
  ingestion: { scopeId: string; file: File; recordId?: string } | null

  // Database configuration state
  dbConfigured: boolean | null // null = loading, true = scopes exist, false = empty DB
  totalRecordCount: number
  scopeRecordCounts: Record<string, number>
  configSwitching: string | null // non-null = switching to this config name

  // Configuration AP — simulator-driven diff session id + cross-pane signals.
  activeDiffSessionId: string | null
  /** Set by seeded-prompt buttons; ChatRail inserts into composer + clears. */
  pendingChatPrefill: string | null
  pendingChatQA: { question: string; answer: string } | null

  // ─── v4.1 cross-pane signals ──────────────────────────────────────────
  /** Set by AktionenTab on drop (and by any other pane that wants to hand
   *  a file off to the chat layer). ChatRail consumes + uploads + clears.
   *  Multi-file: every file in the drop is forwarded as a separate
   *  attachment in the same FormData POST. */
  pendingChatAttachments: File[]
  /** Queue of agent-only messages the simulation engine wants ChatRail to
   *  render (no preceding user question). ChatRail consumes sequentially, then clears. */
  pendingAgentBroadcast: {
    apId: string
    messages: Array<{ text: string; delayMs?: number }>
  } | null
  /** Set by the simulation engine when auto-import should be triggered.
   *  SupplierAktionenTab watches this, runs handleImport, then clears it. */
  pendingAutoImportRecordId: string | null

  // Scope visibility (persisted in database)
  hiddenScopes: Set<string>

  // Filter
  filter: FilterState | null

  // Record selection (Select Records wizard)
  recordSelection: RecordSelection | null
  recordSelectionEditorOpen: boolean

  // Rollup (temporary aggregated columns)
  rollupState: RollupState | null

  // ─── Legacy state (kept for internal dual-write, will be removed) ────
  /** @deprecated Use actionState.diffs[scopeId] instead */
  diffState: DiffState | null
  /** @deprecated Use cancelActionDialogOpen instead */
  cancelDiffDialogOpen: boolean
  /** @deprecated Derive from actionState.type/phase instead */
  downloadMode: boolean
  /** @deprecated Use actionState.primaryScopeId instead */
  downloadScopeId: string | null

  // Transient UI state for checkbox selection (record IDs, not PK values)
  selectedRecordIds: Set<string>

  // Conformity letter modal
  conformityLetterOpen: boolean

  // ─── Importer-AP bucket state (v4) ───────────────────────────────────
  /** Record currently being imported via bucket-import flow.
   *  null = idle (bucket list mode). non-null = import mode for that record. */
  activeBucketRecordId: string | null
  newDocumentIds: Set<string>

  // ─── Unified Action State ─────────────────────────────────────────────
  actionState: ActionState | null
  actionEditorOpen: boolean
  cancelActionDialogOpen: boolean

  // ─── Monitor AP state (v6) ────────────────────────────────────────────
  monitorState: {
    apId: string
    activeFindingId: string | null
    search: string
    erledigtExpanded: boolean
  } | null
  monitorOpenCountByAp: Record<string, number>

  /** Signal from CancelDiffDialog to PivotTable to commit accepted changes.
   *  Replaces the old window.__diffCommitAccepted hack. PivotTable watches this
   *  via useEffect and triggers commit when the value changes. */
  _commitAcceptedSignal: number

  /** Signal from ChatRail's accept_all action to AktionenTab to auto-approve
   *  the active diff session. Bumped whenever the chat "alles richtig" intent
   *  is confirmed by the backend. */
  _autoApproveSignal: number

  // Actions
  setDbConfigured: (configured: boolean | null) => void
  setTotalRecordCount: (count: number) => void
  setScopeRecordCounts: (counts: Record<string, number>) => void
  setActiveDiffSessionId: (id: string | null) => void
  setPendingChatPrefill: (text: string | null) => void
  setPendingChatQA: (qa: { question: string; answer: string } | null) => void
  // v4.1 setters
  setPendingChatAttachments: (files: File[]) => void
  setPendingAgentBroadcast: (
    bc: {
      apId: string
      messages: Array<{ text: string; delayMs?: number }>
    } | null,
  ) => void
  setPendingAutoImportRecordId: (id: string | null) => void
  setConfigSwitching: (name: string | null) => void
  toggleScopeVisibility: (scopeId: string) => void
  setScopeHidden: (scopeId: string, hidden: boolean) => void
  initHiddenScopes: (
    scopeIds: string[],
    scopeRecordCounts: Record<string, number>,
    serverHiddenIds?: string[],
  ) => void
  setActiveScopeId: (id: string) => void
  setScopes: (scopes: ScopeListItem[]) => void
  openRecordDetail: (recordId: string) => void
  closeRecordDetail: () => void
  openEdit: (recordId: string | "new") => void
  closeEdit: () => void
  openDelete: (recordId: string) => void
  closeDelete: () => void
  openRelationPreview: (recordId: string, primName: string) => void
  closeRelationPreview: () => void
  openIngestion: (scopeId: string, file: File, recordId?: string) => void
  closeIngestion: () => void
  setFilter: (originScopeId: string, pkValues: string[]) => void
  addFilterPks: (pks: string[]) => void
  removeFilterPk: (pk: string) => void
  clearFilter: () => void

  // Record selection actions
  setRecordSelection: (selection: RecordSelection) => void
  addSameScopePks: (
    scopeId: string,
    pkValues: string[],
    fileName: string,
    mappings: SelectMapping[],
  ) => void
  addCrossScopePkSet: (
    pkSet: PkSet,
    mappings: SelectMapping[],
    nearMisses: import("../types").NearMissWarning[],
    operator: "and" | "or",
  ) => void
  removePkSet: (scopeId: string) => void
  setCrossScopeOperator: (op: "and" | "or") => void
  toggleSelectionPrimitive: (primitiveId: string) => void
  toggleSelectionRollup: (primitiveId: string) => void
  clearRecordSelection: () => void
  openRecordSelectionEditor: () => void
  closeRecordSelectionEditor: () => void

  // Rollup actions
  setRollupState: (state: RollupState) => void
  updateRollupValue: (
    recordId: string,
    primitiveId: string,
    value: string | null,
    updatedAt?: string | null,
  ) => void
  toggleRollupRecord: (relatedScopeId: string, recordId: string) => void
  setRollupIncludedRecords: (
    relatedScopeId: string,
    recordIds: string[],
  ) => void
  clearRollupState: () => void

  // ─── Legacy actions (kept for IngestionModal bridge, will be removed) ─
  /** @deprecated IngestionModal still calls this; it dual-writes to actionState */
  enterDiffMode: (state: DiffState) => void
  /** @deprecated Use cancelAction() instead */
  exitDiffMode: () => void

  // Transient UI actions for checkbox selection
  toggleRecordSelection: (recordId: string) => void
  selectRecords: (recordIds: string[]) => void
  deselectRecords: (recordIds: string[]) => void
  clearSelection: () => void

  // ─── Unified Action dispatchers ──────────────────────────────────────
  startAction: (primaryScopeId: string, type: ActionType) => void
  setPrimaryPkValues: (pkValues: string[]) => void
  addPrimaryPkValues: (pkValues: string[]) => void
  removePrimaryPkValue: (pkValue: string) => void
  togglePrimaryPkValue: (pkValue: string) => void
  lockAnchor: () => void
  setActionPhase: (phase: ActionPhase) => void
  updateScopeMapping: (
    scopeId: string,
    patch: Partial<ActionScopeMapping>,
  ) => void
  removeScopeMapping: (scopeId: string) => void
  restoreScopeMapping: (scopeId: string) => void
  addActionFile: (scopeId: string, fileCtx: ActionFileContext) => void
  setActionScopeDiff: (scopeId: string, diff: ScopeDiffState) => void
  acceptActionDiffCell: (
    scopeId: string,
    recordId: string,
    primitiveId: string,
  ) => void
  declineActionDiffCell: (
    scopeId: string,
    recordId: string,
    primitiveId: string,
  ) => void
  acceptActionDiffRow: (scopeId: string, recordId: string) => void
  acceptAllActionScopeDiffs: (scopeId: string) => void
  editActionDiffCellValue: (
    scopeId: string,
    recordId: string,
    primitiveId: string,
    newValue: string | null,
    typeIncompatible: boolean,
    secondaryOverride?: boolean,
  ) => void
  acceptActionRelationDiffEntry: (
    scopeId: string,
    recordId: string,
    primitiveId: string,
    targetId: string,
  ) => void
  acceptAllActionRelationDiffs: (
    scopeId: string,
    recordId: string,
    primitiveId: string,
  ) => void
  removeScopeDiff: (scopeId: string) => void
  cancelAction: () => void
  openActionEditor: () => void
  closeActionEditor: () => void
  openCancelActionDialog: () => void
  closeCancelActionDialog: () => void

  // Synced hierarchy level filters: {scope_id: selected_label}
  // Shared across all PivotTable instances so that e.g. selecting "Einzelteil"
  // on the BOM tab also filters Supplier→BOM relation counts
  // and transitive counts consistently.
  hierarchyLevelFilters: Record<string, string>
  setHierarchyLevelFilter: (scopeId: string, label: string | null) => void

  // Freshness date filter (for coloring cells by recency)
  freshnessDate: string | null // ISO date string, e.g. "2026-01-08"
  setFreshnessDate: (date: string | null) => void

  // Diff filter mode: which counter pill is active in the DiffToolbar
  // 'rows' = show all rows (default), 'changes' = primary cells, 'updates' = secondary cells
  diffFilterMode: "rows" | "changes" | "updates"
  setDiffFilterMode: (mode: "rows" | "changes" | "updates") => void

  // Conformity letter actions
  openConformityLetter: () => void
  closeConformityLetter: () => void

  // Importer-AP bucket actions (v4)
  setActiveBucket: (recordId: string) => void
  clearActiveBucket: () => void
  setNewDocumentIds: (ids: Set<string>) => void
  clearNewDocumentIds: () => void

  // ─── Exporter-AP state (v5) ────────────────────────────────────────────
  exporterThreshold: string
  activeExportRecordId: string | null
  outdatedRecordIds: Set<string>
  exporterSelectionMode: "freshness" | "custom"

  setExportThreshold: (date: string) => void
  setActiveExport: (recordId: string) => void
  clearActiveExport: () => void
  toggleOutdatedRecord: (recordId: string) => void
  setExportSelectionMode: (mode: "freshness" | "custom") => void

  // ─── Monitor AP actions (v6) ───────────────────────────────────────────
  initMonitorState: (apId: string) => void
  setActiveFinding: (findingId: string) => void
  clearActiveFinding: () => void
  setMonitorSearch: (search: string) => void
  setErledigtExpanded: (expanded: boolean) => void
  setMonitorOpenCount: (apId: string, count: number) => void
}

// Helper: update a single DiffCell in a scoped diff (ActionState)
function updateActionDiffCell(
  state: UIState,
  scopeId: string,
  recordId: string,
  primitiveId: string,
  patch: Partial<DiffCell>,
): Partial<UIState> {
  const as = state.actionState
  if (!as) return state
  const sd = as.diffs[scopeId]
  if (!sd) return state
  const rec = sd.records[recordId]
  if (!rec) return state
  const cell = rec.cells[primitiveId]
  if (!cell) return state
  return {
    actionState: {
      ...as,
      diffs: {
        ...as.diffs,
        [scopeId]: {
          ...sd,
          records: {
            ...sd.records,
            [recordId]: {
              ...rec,
              cells: { ...rec.cells, [primitiveId]: { ...cell, ...patch } },
            },
          },
        },
      },
    },
  }
}

let actionCounter = 0

// Helper: update a single DiffCell in nested state
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function updateDiffCell(
  state: UIState,
  recordId: string,
  primitiveId: string,
  patch: Partial<DiffCell>,
): Partial<UIState> {
  const ds = state.diffState
  if (!ds) return state
  const rec = ds.records[recordId]
  if (!rec) return state
  const cell = rec.cells[primitiveId]
  if (!cell) return state
  return {
    diffState: {
      ...ds,
      records: {
        ...ds.records,
        [recordId]: {
          ...rec,
          cells: { ...rec.cells, [primitiveId]: { ...cell, ...patch } },
        },
      },
    },
  }
}

// Accepts one or more relation entries across all scope diffs, covering both
// the direct target (scopeId/recordId/primitiveId/targetId when provided) and
// any entry sharing a mirrorKey with an already-accepted one.
function acceptEntriesIn(
  diffs: Record<string, ScopeDiffState>,
  mirrorKeys: Set<string> | null,
  direct: {
    scopeId: string
    recordId: string
    primitiveId: string
    targetId: string | null
  },
): Record<string, ScopeDiffState> {
  const next: Record<string, ScopeDiffState> = {}
  for (const [sid, sd] of Object.entries(diffs)) {
    if (!sd.relationDiffs) {
      next[sid] = sd
      continue
    }
    let scopeChanged = false
    const newRelationDiffs: typeof sd.relationDiffs = {}
    for (const [recId, primMap] of Object.entries(sd.relationDiffs)) {
      const newPrimMap: typeof primMap = {}
      let recChanged = false
      for (const [primId, rd] of Object.entries(primMap)) {
        let rdChanged = false
        const newEntries = rd.entries.map((e) => {
          const isDirect =
            sid === direct.scopeId &&
            recId === direct.recordId &&
            primId === direct.primitiveId &&
            (direct.targetId === null
              ? e.accepted === null
              : e.targetId === direct.targetId)
          const isMirror = !!(
            mirrorKeys &&
            e.mirrorKey &&
            mirrorKeys.has(e.mirrorKey)
          )
          if ((isDirect || isMirror) && e.accepted !== true) {
            rdChanged = true
            return { ...e, accepted: true as const }
          }
          return e
        })
        newPrimMap[primId] = rdChanged ? { ...rd, entries: newEntries } : rd
        if (rdChanged) recChanged = true
      }
      newRelationDiffs[recId] = recChanged ? newPrimMap : primMap
      if (recChanged) scopeChanged = true
    }
    next[sid] = scopeChanged ? { ...sd, relationDiffs: newRelationDiffs } : sd
  }
  return next
}

function _defaultExporterThreshold(): string {
  const d = new Date()
  d.setFullYear(d.getFullYear() - 1)
  return d.toISOString().slice(0, 10)
}

export const useStore = create<UIState>((set) => ({
  activeScopeId: "component",
  scopes: [],
  dbConfigured: null,
  totalRecordCount: 0,
  scopeRecordCounts: {},
  configSwitching: null,
  hiddenScopes: new Set<string>(),
  detailRecordId: null,
  editRecordId: null,
  deleteRecordId: null,
  relationPreview: null,
  ingestion: null,
  filter: null,
  recordSelection: null,
  recordSelectionEditorOpen: false,
  rollupState: null,
  diffState: null,
  cancelDiffDialogOpen: false,
  downloadMode: false,
  downloadScopeId: null,
  selectedRecordIds: new Set<string>(),
  conformityLetterOpen: false,
  activeBucketRecordId: null,
  newDocumentIds: new Set<string>(),
  hierarchyLevelFilters: {},
  freshnessDate: null,
  diffFilterMode: "rows" as const,
  actionState: null,
  actionEditorOpen: false,
  cancelActionDialogOpen: false,
  _commitAcceptedSignal: 0,
  _autoApproveSignal: 0,
  activeDiffSessionId: null,
  pendingChatPrefill: null,
  pendingChatQA: null,
  pendingChatAttachments: [],
  pendingAgentBroadcast: null,
  pendingAutoImportRecordId: null,
  exporterThreshold: _defaultExporterThreshold(),
  activeExportRecordId: null,
  outdatedRecordIds: new Set<string>(),
  exporterSelectionMode: "freshness" as const,
  monitorState: null,
  monitorOpenCountByAp: {},

  setDbConfigured: (configured) => set({ dbConfigured: configured }),
  setTotalRecordCount: (count) => set({ totalRecordCount: count }),
  setScopeRecordCounts: (counts) => set({ scopeRecordCounts: counts }),
  setActiveDiffSessionId: (id) => set({ activeDiffSessionId: id }),
  setPendingChatPrefill: (text) => set({ pendingChatPrefill: text }),
  setPendingChatQA: (qa) => set({ pendingChatQA: qa }),
  setPendingChatAttachments: (files) => set({ pendingChatAttachments: files }),
  setPendingAgentBroadcast: (bc) => set({ pendingAgentBroadcast: bc }),
  setPendingAutoImportRecordId: (id) => set({ pendingAutoImportRecordId: id }),
  setConfigSwitching: (name) => set({ configSwitching: name }),
  toggleScopeVisibility: (scopeId) =>
    set((state) => {
      const next = new Set(state.hiddenScopes)
      const nowHidden = !next.has(scopeId)
      if (nowHidden) next.add(scopeId)
      else next.delete(scopeId)
      api.setScopeHidden(scopeId, nowHidden).catch(() => {})
      return { hiddenScopes: next }
    }),
  setScopeHidden: (scopeId, hidden) =>
    set((state) => {
      const next = new Set(state.hiddenScopes)
      if (hidden) next.add(scopeId)
      else next.delete(scopeId)
      api.setScopeHidden(scopeId, hidden).catch(() => {})
      return { hiddenScopes: next }
    }),
  initHiddenScopes: (scopeIds, scopeRecordCounts, serverHiddenIds) =>
    set((state) => {
      const persisted = new Set(serverHiddenIds ?? [])
      if (persisted.size === 0) {
        return {
          hiddenScopes: new Set<string>(),
          activeScopeId: scopeIds[0] ?? state.activeScopeId,
        }
      }
      // Use server state
      const result: Partial<UIState> = { hiddenScopes: persisted }
      if (persisted.has(state.activeScopeId)) {
        const firstVisible = scopeIds.find((id) => !persisted.has(id))
        if (firstVisible) result.activeScopeId = firstVisible
      }
      return result
    }),
  setActiveScopeId: (id) =>
    set((state) => {
      // In download mode, changing scope clears selection (single-scope enforcement)
      if (state.downloadMode && id !== state.downloadScopeId) {
        return {
          activeScopeId: id,
          selectedRecordIds: new Set<string>(),
          downloadScopeId: id,
          // Also update actionState primary scope
          actionState: state.actionState
            ? { ...state.actionState, primaryScopeId: id, primaryPkValues: [] }
            : null,
        }
      }
      return { activeScopeId: id }
    }),
  setScopes: (scopes) => set({ scopes }),
  openRecordDetail: (recordId) => set({ detailRecordId: recordId }),
  closeRecordDetail: () => set({ detailRecordId: null, deleteRecordId: null }),
  openEdit: (recordId) => set({ editRecordId: recordId }),
  closeEdit: () => set({ editRecordId: null }),
  openDelete: (recordId) => set({ deleteRecordId: recordId }),
  closeDelete: () => set({ deleteRecordId: null }),
  openRelationPreview: (recordId, primName) =>
    set({ relationPreview: { recordId, primName } }),
  closeRelationPreview: () => set({ relationPreview: null }),
  openIngestion: (scopeId, file, recordId) =>
    set({
      ingestion: { scopeId, file, recordId },
      diffState: null,
      cancelDiffDialogOpen: false,
    }),
  closeIngestion: () => set({ ingestion: null }),
  setFilter: (originScopeId, pkValues) =>
    set({ filter: { originScopeId, pkValues }, activeScopeId: originScopeId }),
  addFilterPks: (pks) =>
    set((state) => {
      if (!state.filter) return state
      const merged = [...new Set([...state.filter.pkValues, ...pks])]
      return { filter: { ...state.filter, pkValues: merged } }
    }),
  removeFilterPk: (pk) =>
    set((state) => {
      if (!state.filter) return state
      const remaining = state.filter.pkValues.filter((v) => v !== pk)
      if (remaining.length === 0) return { filter: null }
      return { filter: { ...state.filter, pkValues: remaining } }
    }),
  clearFilter: () => set({ filter: null }),

  // Record selection actions (dual-write: old recordSelection + actionState)
  setRecordSelection: (selection) =>
    set((state) => {
      // Create actionState from the first pkSet (primary scope)
      const primaryPkSet = selection.pkSets[0]
      const actionState: ActionState = {
        id: `action-${++actionCounter}-${Date.now()}`,
        primaryScopeId: primaryPkSet?.scopeId ?? state.activeScopeId,
        primaryPkValues: primaryPkSet?.pkValues ?? [],
        type: "select" as const,
        phase: "select" as const,
        anchorLocked: true,
        scopeMappings: {},
        files: {},
        diffs: {},
      }
      return {
        recordSelection: selection,
        filter: null,
        actionState,
        // Clear conflicting state
        downloadMode: false,
        downloadScopeId: null,
        selectedRecordIds: new Set<string>(),
        diffState: null,
        cancelDiffDialogOpen: false,
      }
    }),
  addSameScopePks: (scopeId, pkValues, fileName, mappings) =>
    set((state) => {
      if (!state.recordSelection) return state
      const pkSets = state.recordSelection.pkSets.map((ps) =>
        ps.scopeId === scopeId
          ? {
              ...ps,
              pkValues: [...new Set([...ps.pkValues, ...pkValues])],
              fileNames: [...ps.fileNames, fileName],
            }
          : ps,
      )
      const existingIds = new Set(
        state.recordSelection.mappings.map((m) => m.primitiveId),
      )
      const newMappings = mappings.filter(
        (m) => !existingIds.has(m.primitiveId),
      )
      const allMappings = [...state.recordSelection.mappings, ...newMappings]
      const visibleIds = allMappings
        .filter((m) => m.visible)
        .map((m) => m.primitiveId)
      // Sync to actionState: merge PKs
      const as = state.actionState
      const actionPatch: Partial<UIState> = as
        ? {
            actionState: {
              ...as,
              primaryPkValues: [
                ...new Set([...as.primaryPkValues, ...pkValues]),
              ],
            },
          }
        : {}
      return {
        recordSelection: {
          ...state.recordSelection,
          pkSets,
          mappings: allMappings,
          visiblePrimitiveIds: visibleIds,
        },
        ...actionPatch,
      }
    }),
  addCrossScopePkSet: (pkSet, mappings, nearMisses, operator) =>
    set((state) => {
      if (!state.recordSelection) return state
      const pkSets = [...state.recordSelection.pkSets, pkSet]
      const existingIds = new Set(
        state.recordSelection.mappings.map((m) => m.primitiveId),
      )
      const newMappings = mappings.filter(
        (m) => !existingIds.has(m.primitiveId),
      )
      const allMappings = [...state.recordSelection.mappings, ...newMappings]
      const visibleIds = allMappings
        .filter((m) => m.visible)
        .map((m) => m.primitiveId)
      return {
        recordSelection: {
          ...state.recordSelection,
          pkSets,
          crossScopeOperator: operator,
          mappings: allMappings,
          visiblePrimitiveIds: visibleIds,
          nearMisses: [...state.recordSelection.nearMisses, ...nearMisses],
        },
      }
    }),
  removePkSet: (scopeId) =>
    set((state) => {
      if (!state.recordSelection) return state
      const pkSets = state.recordSelection.pkSets.filter(
        (ps) => ps.scopeId !== scopeId,
      )
      if (pkSets.length === 0)
        return {
          recordSelection: null,
          recordSelectionEditorOpen: false,
          actionState: null,
        }
      const removedKey = scopeId
      const mappings = state.recordSelection.mappings.filter(
        (m) => m.originPkSetKey !== removedKey,
      )
      const visibleIds = mappings
        .filter((m) => m.visible)
        .map((m) => m.primitiveId)
      const crossScopeOperator =
        pkSets.length <= 1 ? "or" : state.recordSelection.crossScopeOperator
      return {
        recordSelection: {
          ...state.recordSelection,
          pkSets,
          mappings,
          visiblePrimitiveIds: visibleIds,
          crossScopeOperator,
        },
      }
    }),
  setCrossScopeOperator: (op) =>
    set((state) => {
      if (!state.recordSelection) return state
      return {
        recordSelection: { ...state.recordSelection, crossScopeOperator: op },
      }
    }),
  toggleSelectionPrimitive: (primitiveId) =>
    set((state) => {
      if (!state.recordSelection) return state
      const mappings = state.recordSelection.mappings.map((m) =>
        m.primitiveId === primitiveId ? { ...m, visible: !m.visible } : m,
      )
      const visibleIds = mappings
        .filter((m) => m.visible)
        .map((m) => m.primitiveId)
      return {
        recordSelection: {
          ...state.recordSelection,
          mappings,
          visiblePrimitiveIds: visibleIds,
        },
      }
    }),
  toggleSelectionRollup: (primitiveId) =>
    set((state) => {
      if (!state.recordSelection) return state
      const mappings = state.recordSelection.mappings.map((m) =>
        m.primitiveId === primitiveId ? { ...m, isRollup: !m.isRollup } : m,
      )
      return {
        recordSelection: { ...state.recordSelection, mappings },
      }
    }),
  clearRecordSelection: () =>
    set({
      recordSelection: null,
      recordSelectionEditorOpen: false,
      rollupState: null,
      actionState: null,
      actionEditorOpen: false,
      cancelActionDialogOpen: false,
    }),
  openRecordSelectionEditor: () => set({ recordSelectionEditorOpen: true }),
  closeRecordSelectionEditor: () => set({ recordSelectionEditorOpen: false }),

  // Rollup actions
  setRollupState: (rollupState) =>
    set({
      rollupState: {
        ...rollupState,
        includedRecordIds: rollupState.includedRecordIds ?? {},
      },
    }),
  updateRollupValue: (recordId, primitiveId, value, updatedAt) =>
    set((state) => {
      if (!state.rollupState) return state
      const values = { ...state.rollupState.values }
      const existing = values[recordId]?.[primitiveId]
      values[recordId] = {
        ...(values[recordId] ?? {}),
        [primitiveId]: {
          value,
          updated_at: updatedAt ?? existing?.updated_at ?? null,
        },
      }
      return { rollupState: { ...state.rollupState, values } }
    }),
  toggleRollupRecord: (relatedScopeId, recordId) =>
    set((state) => {
      if (!state.rollupState) return state
      const included = { ...state.rollupState.includedRecordIds }
      const ids = included[relatedScopeId] ?? []
      const idx = ids.indexOf(recordId)
      included[relatedScopeId] =
        idx >= 0
          ? ids.filter((id: string) => id !== recordId)
          : [...ids, recordId]
      return {
        rollupState: { ...state.rollupState, includedRecordIds: included },
      }
    }),
  setRollupIncludedRecords: (relatedScopeId, recordIds) =>
    set((state) => {
      if (!state.rollupState) return state
      const included = { ...state.rollupState.includedRecordIds }
      included[relatedScopeId] = recordIds
      return {
        rollupState: { ...state.rollupState, includedRecordIds: included },
      }
    }),
  clearRollupState: () => set({ rollupState: null }),

  // Diff mode actions
  enterDiffMode: (diffState) =>
    set((state) => {
      // Dual-write: also populate actionState for Phase 2 migration
      const scopeDiff: ScopeDiffState = {
        scopeId: diffState.scopeId,
        fileName: diffState.fileName,
        records: diffState.records,
        rawExtractions: diffState.rawExtractions,
        anchoredRecords: diffState.anchoredRecords,
        relationDiffs: diffState.relationDiffs,
        exportMode: diffState.exportMode,
        exportParsedHeaders: diffState.exportParsedHeaders,
        exportParsedRows: diffState.exportParsedRows,
        exportDelimiter: diffState.exportDelimiter,
        targetFormat: diffState.targetFormat,
        exportColumns: diffState.exportColumns,
        exportRecordIds: diffState.exportRecordIds,
      }
      const actionType = diffState.exportMode
        ? ("export" as const)
        : ("import" as const)
      const existing = state.actionState
      const actionState: ActionState = existing
        ? {
            ...existing,
            phase: "diff" as const,
            diffs: { ...existing.diffs, [diffState.scopeId]: scopeDiff },
          }
        : {
            id: `action-${++actionCounter}-${Date.now()}`,
            primaryScopeId: diffState.scopeId,
            primaryPkValues: [],
            type: actionType,
            phase: "diff" as const,
            anchorLocked: true,
            scopeMappings: {},
            files: {},
            diffs: { [diffState.scopeId]: scopeDiff },
          }
      return {
        diffState,
        actionState,
        ingestion: null,
        activeScopeId: diffState.scopeId,
        detailRecordId: null,
        deleteRecordId: null,
      }
    }),
  exitDiffMode: () =>
    set({
      diffState: null,
      cancelDiffDialogOpen: false,
      rollupState: null,
      actionState: null,
      cancelActionDialogOpen: false,
      diffFilterMode: "rows" as const,
    }),
  toggleRecordSelection: (recordId) =>
    set((state) => {
      const next = new Set(state.selectedRecordIds)
      if (next.has(recordId)) next.delete(recordId)
      else next.add(recordId)
      return { selectedRecordIds: next }
    }),
  selectRecords: (recordIds) =>
    set((state) => {
      const next = new Set(state.selectedRecordIds)
      for (const id of recordIds) next.add(id)
      return { selectedRecordIds: next }
    }),
  deselectRecords: (recordIds) =>
    set((state) => {
      const next = new Set(state.selectedRecordIds)
      for (const id of recordIds) next.delete(id)
      return { selectedRecordIds: next }
    }),
  clearSelection: () => set({ selectedRecordIds: new Set<string>() }),

  // ─── Unified Action implementations ──────────────────────────────────

  startAction: (primaryScopeId, type) =>
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    set((_state) => ({
      actionState: {
        id: `action-${++actionCounter}-${Date.now()}`,
        primaryScopeId,
        primaryPkValues: [],
        type,
        phase: "select",
        anchorLocked: false,
        scopeMappings: {},
        files: {},
        diffs: {},
      },
      // Clear any conflicting old state
      recordSelection: null,
      recordSelectionEditorOpen: false,
      diffState: null,
      cancelDiffDialogOpen: false,
      downloadMode: false,
      downloadScopeId: null,
      selectedRecordIds: new Set<string>(),
      rollupState: null,
      filter: null,
      activeScopeId: primaryScopeId,
    })),

  setPrimaryPkValues: (pkValues) =>
    set((state) => {
      if (!state.actionState) return state
      return {
        actionState: { ...state.actionState, primaryPkValues: pkValues },
      }
    }),

  addPrimaryPkValues: (pkValues) =>
    set((state) => {
      if (!state.actionState) return state
      const merged = [
        ...new Set([...state.actionState.primaryPkValues, ...pkValues]),
      ]
      return { actionState: { ...state.actionState, primaryPkValues: merged } }
    }),

  removePrimaryPkValue: (pkValue) =>
    set((state) => {
      if (!state.actionState) return state
      const remaining = state.actionState.primaryPkValues.filter(
        (v) => v !== pkValue,
      )
      return {
        actionState: { ...state.actionState, primaryPkValues: remaining },
      }
    }),

  togglePrimaryPkValue: (pkValue) =>
    set((state) => {
      if (!state.actionState) return state
      const current = state.actionState.primaryPkValues
      const next = current.includes(pkValue)
        ? current.filter((v) => v !== pkValue)
        : [...current, pkValue]
      return { actionState: { ...state.actionState, primaryPkValues: next } }
    }),

  lockAnchor: () =>
    set((state) => {
      if (!state.actionState) return state
      return { actionState: { ...state.actionState, anchorLocked: true } }
    }),

  setActionPhase: (phase) =>
    set((state) => {
      if (!state.actionState) return state
      return { actionState: { ...state.actionState, phase } }
    }),

  updateScopeMapping: (scopeId, patch) =>
    set((state) => {
      if (!state.actionState) return state
      const existing = state.actionState.scopeMappings[scopeId]
      if (!existing) return state
      return {
        actionState: {
          ...state.actionState,
          scopeMappings: {
            ...state.actionState.scopeMappings,
            [scopeId]: { ...existing, ...patch },
          },
        },
      }
    }),

  removeScopeMapping: (scopeId) =>
    set((state) => {
      if (!state.actionState) return state
      const existing = state.actionState.scopeMappings[scopeId]
      if (!existing) return state
      return {
        actionState: {
          ...state.actionState,
          scopeMappings: {
            ...state.actionState.scopeMappings,
            [scopeId]: { ...existing, status: "removed" },
          },
        },
      }
    }),

  restoreScopeMapping: (scopeId) =>
    set((state) => {
      if (!state.actionState) return state
      const existing = state.actionState.scopeMappings[scopeId]
      if (!existing) return state
      return {
        actionState: {
          ...state.actionState,
          scopeMappings: {
            ...state.actionState.scopeMappings,
            [scopeId]: { ...existing, status: "default" },
          },
        },
      }
    }),

  addActionFile: (scopeId, fileCtx) =>
    set((state) => {
      if (!state.actionState) return state
      return {
        actionState: {
          ...state.actionState,
          files: { ...state.actionState.files, [scopeId]: fileCtx },
        },
      }
    }),

  setActionScopeDiff: (scopeId, diff) =>
    set((state) => {
      if (!state.actionState) return state
      return {
        actionState: {
          ...state.actionState,
          phase: "diff",
          diffs: { ...state.actionState.diffs, [scopeId]: diff },
        },
      }
    }),

  acceptActionDiffCell: (scopeId, recordId, primitiveId) =>
    set((state) =>
      updateActionDiffCell(state, scopeId, recordId, primitiveId, {
        accepted: true,
        declined: false,
      }),
    ),

  declineActionDiffCell: (scopeId, recordId, primitiveId) =>
    set((state) =>
      updateActionDiffCell(state, scopeId, recordId, primitiveId, {
        accepted: true,
        declined: true,
      }),
    ),

  acceptActionDiffRow: (scopeId, recordId) =>
    set((state) => {
      const as = state.actionState
      if (!as) return state
      const sd = as.diffs[scopeId]
      if (!sd) return state
      const rec = sd.records[recordId]
      if (!rec) return state
      const cells = { ...rec.cells }
      for (const [pid, cell] of Object.entries(cells)) {
        if (cell.accepted === null) {
          cells[pid] = { ...cell, accepted: true }
        }
      }
      return {
        actionState: {
          ...as,
          diffs: {
            ...as.diffs,
            [scopeId]: {
              ...sd,
              records: {
                ...sd.records,
                [recordId]: { ...rec, cells, rowFreshnessAccepted: true },
              },
            },
          },
        },
      }
    }),

  acceptAllActionScopeDiffs: (scopeId) =>
    set((state) => {
      const as = state.actionState
      if (!as) return state
      const sd = as.diffs[scopeId]
      if (!sd) return state
      // Accept ALL unresolved cells + row freshness
      const records = { ...sd.records }
      for (const [rid, rec] of Object.entries(records)) {
        const cells = { ...rec.cells }
        for (const [pid, cell] of Object.entries(cells)) {
          if (cell.accepted === null) {
            cells[pid] = { ...cell, accepted: true }
          }
        }
        records[rid] = { ...rec, cells, rowFreshnessAccepted: true }
      }
      // Also accept all relation diffs
      let relationDiffs = sd.relationDiffs
      if (relationDiffs) {
        relationDiffs = { ...relationDiffs }
        for (const [rid, primMap] of Object.entries(relationDiffs)) {
          const newPrimMap = { ...primMap }
          for (const [pid, rd] of Object.entries(newPrimMap)) {
            newPrimMap[pid] = {
              ...rd,
              entries: rd.entries.map((e) =>
                e.accepted === null ? { ...e, accepted: true } : e,
              ),
            }
          }
          relationDiffs[rid] = newPrimMap
        }
      }
      return {
        actionState: {
          ...as,
          diffs: {
            ...as.diffs,
            [scopeId]: { ...sd, records, relationDiffs },
          },
        },
      }
    }),

  editActionDiffCellValue: (
    scopeId,
    recordId,
    primitiveId,
    newValue,
    typeIncompatible,
    secondaryOverride,
  ) =>
    set((state) =>
      updateActionDiffCell(state, scopeId, recordId, primitiveId, {
        fileValue: newValue,
        typeIncompatible,
        secondary: secondaryOverride ?? false, // default: promotes secondary cells to primary
        declined: false, // editing always clears the declined state
      }),
    ),

  acceptActionRelationDiffEntry: (scopeId, recordId, primitiveId, targetId) =>
    set((state) => {
      const as = state.actionState
      if (!as) return state
      const sd = as.diffs[scopeId]
      const entry = sd?.relationDiffs?.[recordId]?.[primitiveId]?.entries.find(
        (e) => e.targetId === targetId,
      )
      if (!entry) return state
      const mirrorKeys = entry.mirrorKey ? new Set([entry.mirrorKey]) : null
      return {
        actionState: {
          ...as,
          diffs: acceptEntriesIn(as.diffs, mirrorKeys, {
            scopeId,
            recordId,
            primitiveId,
            targetId,
          }),
        },
      }
    }),

  acceptAllActionRelationDiffs: (scopeId, recordId, primitiveId) =>
    set((state) => {
      const as = state.actionState
      if (!as) return state
      const sd = as.diffs[scopeId]
      const rd = sd?.relationDiffs?.[recordId]?.[primitiveId]
      if (!rd) return state
      const mirrorKeys = new Set<string>()
      for (const e of rd.entries) {
        if (e.accepted === null && e.mirrorKey) mirrorKeys.add(e.mirrorKey)
      }
      return {
        actionState: {
          ...as,
          diffs: acceptEntriesIn(as.diffs, mirrorKeys, {
            scopeId,
            recordId,
            primitiveId,
            targetId: null,
          }),
        },
      }
    }),

  removeScopeDiff: (scopeId) =>
    set((state) => {
      const as = state.actionState
      if (!as) return state
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [scopeId]: _removed, ...remainingDiffs } = as.diffs
      // Primary removal → also clean up secondary diffs
      const isPrimaryRemoval = scopeId === as.primaryScopeId
      const finalDiffs = isPrimaryRemoval
        ? Object.fromEntries(
            Object.entries(remainingDiffs).filter(
              ([, sd]) => !sd.secondaryDiff,
            ),
          )
        : remainingDiffs
      const hasRemainingDiffs = Object.keys(finalDiffs).length > 0
      return {
        actionState: hasRemainingDiffs ? { ...as, diffs: finalDiffs } : null, // action complete
        cancelActionDialogOpen: false,
      }
    }),

  cancelAction: () =>
    set({
      actionState: null,
      actionEditorOpen: false,
      cancelActionDialogOpen: false,
      diffFilterMode: "rows" as const,
    }),

  openActionEditor: () => set({ actionEditorOpen: true }),
  closeActionEditor: () => set({ actionEditorOpen: false }),
  openCancelActionDialog: () => set({ cancelActionDialogOpen: true }),
  closeCancelActionDialog: () => set({ cancelActionDialogOpen: false }),

  // Freshness date filter
  setHierarchyLevelFilter: (scopeId, label) =>
    set((state) => {
      const next = { ...state.hierarchyLevelFilters }
      if (label === null) {
        delete next[scopeId]
      } else {
        next[scopeId] = label
      }
      return { hierarchyLevelFilters: next }
    }),
  setFreshnessDate: (date) => set({ freshnessDate: date }),
  setDiffFilterMode: (mode) => set({ diffFilterMode: mode }),

  // Conformity letter
  openConformityLetter: () => set({ conformityLetterOpen: true }),
  closeConformityLetter: () => set({ conformityLetterOpen: false }),

  setActiveBucket: (recordId) => set({ activeBucketRecordId: recordId }),
  clearActiveBucket: () => set({ activeBucketRecordId: null }),
  setNewDocumentIds: (ids) => set({ newDocumentIds: ids }),
  clearNewDocumentIds: () => set({ newDocumentIds: new Set<string>() }),

  setExportThreshold: (date) => set({ exporterThreshold: date }),
  setActiveExport: (recordId) => set({ activeExportRecordId: recordId }),
  clearActiveExport: () => set({ activeExportRecordId: null }),
  toggleOutdatedRecord: (recordId) =>
    set((state) => {
      const next = new Set(state.outdatedRecordIds)
      if (next.has(recordId)) next.delete(recordId)
      else next.add(recordId)
      return { outdatedRecordIds: next }
    }),
  setExportSelectionMode: (mode) => set({ exporterSelectionMode: mode }),

  // ─── Monitor AP actions (v6) ───────────────────────────────────────────
  initMonitorState: (apId) =>
    set((state) => {
      if (state.monitorState?.apId === apId) return {}
      return {
        monitorState: {
          apId,
          activeFindingId: null,
          search: "",
          erledigtExpanded: false,
        },
      }
    }),
  setActiveFinding: (findingId) =>
    set((state) => ({
      monitorState: state.monitorState
        ? { ...state.monitorState, activeFindingId: findingId }
        : null,
    })),
  clearActiveFinding: () =>
    set((state) => ({
      monitorState: state.monitorState
        ? { ...state.monitorState, activeFindingId: null }
        : null,
    })),
  setMonitorSearch: (search) =>
    set((state) => ({
      monitorState: state.monitorState
        ? { ...state.monitorState, search }
        : null,
    })),
  setErledigtExpanded: (expanded) =>
    set((state) => ({
      monitorState: state.monitorState
        ? { ...state.monitorState, erledigtExpanded: expanded }
        : null,
    })),
  setMonitorOpenCount: (apId, count) =>
    set((state) => ({
      monitorOpenCountByAp: { ...state.monitorOpenCountByAp, [apId]: count },
    })),
}))
